import React, { useRef, useEffect, useState } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { OrbitControls, Text, Html } from '@react-three/drei';
import * as THREE from 'three';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, Download, Ruler, RotateCcw, Zap } from 'lucide-react';

// Point Cloud Component
const BurnPointCloud = ({ modelData, viewMode, onPointClick }) => {
  const meshRef = useRef();
  const [geometry, setGeometry] = useState(null);

  useEffect(() => {
    if (!modelData || !modelData.vertices) return;

    const geo = new THREE.BufferGeometry();
    
    // Set vertices
    const vertices = new Float32Array(modelData.vertices);
    geo.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    
    // Set colors
    if (modelData.colors) {
      const colors = new Float32Array(modelData.colors);
      geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    }
    
    // Set normals if available
    if (modelData.normals) {
      const normals = new Float32Array(modelData.normals);
      geo.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    } else {
      geo.computeVertexNormals();
    }

    setGeometry(geo);
  }, [modelData]);

  useFrame((state, delta) => {
    // Subtle rotation for better depth perception
    if (meshRef.current && viewMode === 'auto-rotate') {
      meshRef.current.rotation.z += delta * 0.1;
    }
  });

  if (!geometry) return null;

  // Material based on view mode
  let material;
  switch (viewMode) {
    case 'depth':
      material = new THREE.PointsMaterial({
        size: 0.02,
        vertexColors: false,
        color: new THREE.Color().setHSL(0.6, 1.0, 0.5), // Blue for depth
      });
      break;
    case 'wireframe':
      material = new THREE.PointsMaterial({
        size: 0.01,
        vertexColors: true,
        transparent: true,
        opacity: 0.8,
      });
      break;
    default: // textured
      material = new THREE.PointsMaterial({
        size: 0.025,
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
      });
  }

  return (
    <points
      ref={meshRef}
      geometry={geometry}
      material={material}
      onClick={onPointClick}
    />
  );
};

// Measurement Tool Component
const MeasurementTool = ({ points, onClear }) => {
  if (points.length < 2) return null;

  const distance = points[0].distanceTo(points[1]) * 10; // Convert to mm (assuming model is in cm)

  return (
    <group>
      {/* Line between points */}
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            array={new Float32Array([
              points[0].x, points[0].y, points[0].z,
              points[1].x, points[1].y, points[1].z,
            ])}
            count={2}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color="yellow" linewidth={2} />
      </line>
      
      {/* Distance label */}
      <Text
        position={[(points[0].x + points[1].x) / 2, (points[0].y + points[1].y) / 2 + 0.5, (points[0].z + points[1].z) / 2]}
        fontSize={0.3}
        color="yellow"
        anchorX="center"
        anchorY="middle"
      >
        {distance.toFixed(1)} mm
      </Text>
      
      {/* Point markers */}
      {points.map((point, index) => (
        <mesh key={index} position={[point.x, point.y, point.z]}>
          <sphereGeometry args={[0.05]} />
          <meshBasicMaterial color="yellow" />
        </mesh>
      ))}
    </group>
  );
};

// Grid Helper Component
const GridHelper = ({ size = 10 }) => (
  <gridHelper args={[size, 20, 0x444444, 0x888888]} position={[0, 0, -2]} />
);

// Main 3D Viewer Component
const ThreeViewer = ({ modelData, analysisData, onExport }) => {
  const [viewMode, setViewMode] = useState('textured');
  const [measurementMode, setMeasurementMode] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (modelData) {
      setIsLoading(false);
    }
  }, [modelData]);

  const handlePointClick = (event) => {
    if (!measurementMode) return;

    const point = event.point;
    if (measurementPoints.length === 0) {
      setMeasurementPoints([point]);
    } else if (measurementPoints.length === 1) {
      setMeasurementPoints([measurementPoints[0], point]);
    } else {
      // Reset for new measurement
      setMeasurementPoints([point]);
    }
  };

  const clearMeasurements = () => {
    setMeasurementPoints([]);
  };

  const resetView = () => {
    setViewMode('textured');
    setMeasurementMode(false);
    clearMeasurements();
  };

  if (isLoading || !modelData) {
    return (
      <Card className="h-96">
        <CardContent className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading 3D model...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            3D Burn Model Viewer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              variant={viewMode === 'textured' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('textured')}
            >
              Textured View
            </Button>
            <Button
              variant={viewMode === 'depth' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('depth')}
            >
              Depth Map
            </Button>
            <Button
              variant={viewMode === 'wireframe' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('wireframe')}
            >
              Point Cloud
            </Button>
            <Button
              variant={measurementMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMeasurementMode(!measurementMode)}
            >
              <Ruler className="w-4 h-4 mr-1" />
              Measure
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={resetView}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
            {onExport && (
              <Button
                variant="outline"
                size="sm"
                onClick={onExport}
              >
                <Download className="w-4 h-4 mr-1" />
                Export PLY
              </Button>
            )}
          </div>

          {measurementMode && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <Ruler className="w-4 h-4 inline mr-1" />
                Measurement Mode: Click two points on the model to measure distance.
                {measurementPoints.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-2"
                    onClick={clearMeasurements}
                  >
                    Clear
                  </Button>
                )}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3D Viewer */}
      <Card>
        <CardContent className="p-0">
          <div 
            className="h-96 w-full relative rounded-lg overflow-hidden"
            style={{ cursor: measurementMode ? 'crosshair' : 'default' }}
          >
            <Canvas
              camera={{ position: [5, 5, 5], fov: 60 }}
              style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)' }}
            >
              {/* Lighting */}
              <ambientLight intensity={0.6} />
              <directionalLight 
                position={[10, 10, 5]} 
                intensity={1} 
                castShadow
                shadow-mapSize-width={1024}
                shadow-mapSize-height={1024}
              />
              <pointLight position={[-10, -10, -5]} intensity={0.3} />

              {/* Controls */}
              <OrbitControls 
                enablePan={true}
                enableZoom={true}
                enableRotate={true}
                maxDistance={20}
                minDistance={1}
              />

              {/* Grid */}
              <GridHelper size={modelData.bounds ? Math.max(
                Math.abs(modelData.bounds.max_x - modelData.bounds.min_x),
                Math.abs(modelData.bounds.max_y - modelData.bounds.min_y)
              ) : 10} />

              {/* 3D Model */}
              <BurnPointCloud
                modelData={modelData}
                viewMode={viewMode}
                onPointClick={handlePointClick}
              />

              {/* Measurement Tool */}
              {measurementPoints.length > 0 && (
                <MeasurementTool
                  points={measurementPoints}
                  onClear={clearMeasurements}
                />
              )}

              {/* Coordinate System */}
              <axesHelper args={[2]} />
            </Canvas>

            {/* Overlay Info */}
            <div className="absolute top-4 left-4 bg-white bg-opacity-90 rounded-lg p-3 text-sm">
              <div className="font-medium mb-1">Model Info</div>
              <div>Vertices: {modelData.vertex_count?.toLocaleString()}</div>
              <div>View: {viewMode.charAt(0).toUpperCase() + viewMode.slice(1)}</div>
              {measurementPoints.length === 2 && (
                <div className="text-yellow-600 font-medium">
                  Distance: {(measurementPoints[0].distanceTo(measurementPoints[1]) * 10).toFixed(1)} mm
                </div>
              )}
            </div>

            {/* Controls Help */}
            <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white rounded-lg p-2 text-xs">
              <div>🖱️ Left click: Rotate</div>
              <div>🖱️ Right click: Pan</div>
              <div>🔄 Scroll: Zoom</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ThreeViewer;